import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { chain, isNumber, truncate } from 'lodash';
import { Intent, Button, Tag, Icon } from '@blueprintjs/core';
import { buildAmount } from '@retro-ui/shared';

import Table from '@f/components/shared/table/Table';
import ContextualCard from '@f/components/shared/contextual-card/ContextualCard';
import ParameterList from '@f/components/shared/parameter-list/ParameterList';

import cls from './PriceList.module.css';

function sortItems(items) {
  return chain(items)
    .shuffle()
    .sortBy(['availabilityTier', 'supplierName'])
    .sortBy(item => Number(item.price) / Number(item.amount))
    .value();
}

function buildItems(data, selectedDatasets) {
  if (!Array.isArray(selectedDatasets) || selectedDatasets.length === 0) {
    return sortItems(data);
  }

  const items = chain(data)
    .partition(item => {
      const selectedDataset = selectedDatasets.find(dataset => dataset.id === item.datasetId);

      if (!selectedDataset) {
        return false;
      }

      if (!Array.isArray(selectedDataset.selected_suppliers)) {
        return true;
      }

      return selectedDataset.selected_suppliers.includes(item.supplierId);
    })
    .thru(([topItems, restItems]) => [
      topItems.map(item => ({
        ...item,
        selectedSupplier: true,
      })),
      restItems,
    ])
    .thru(groups => groups.map(group => sortItems(group)))
    .flatten()
    .value();

  if (items.every(item => item.selectedSupplier === true)) {
    return items.map(item => {
      return { ...item, selectedSupplier: false };
    });
  }

  return items;
}

function buildAvailabilityDescription(availabilityTier) {
  switch (availabilityTier) {
    case 1:
      return '1-5 business days';
    case 2:
      return '2-10 business days';
    case 3:
      return '4 weeks';
    case 4:
      return '12 weeks (may require synthesis)';
    case 5:
      return 'Require custom quote (POA)';
    default:
      return 'Unknown';
  }
}

function buildRowProps(expansionMap, onExtendedInfoClose) {
  return (item, idx) => {
    const onClick = () => onExtendedInfoClose(item, idx);

    return {
      className: cx({
        [cls.expanded]: expansionMap[String(idx)],
        [cls.rowInStock]: item.availabilityTier < 4,
        [cls.rowReSynthesis]: item.availabilityTier === 4,
        [cls.rowCustomAvailability]: item.availabilityTier > 4 || item.availabilityTier == null,
      }),
      after: expansionMap[String(idx)] && (
        <ContextualCard className={cls.extendedInfoCard}>
          <ContextualCard.Main className={cls.extendedInfoContent}>
            <ParameterList
              className={cls.extendedInfoList}
              schema={[
                {
                  label: 'Supplier',
                  renderer: item => item.supplierName || 'n/a',
                },
                {
                  label: 'Availability',
                  renderer: item => (
                    <span className={cls.availabilityDescription}>
                      <Icon icon="time" />
                      <span>{buildAvailabilityDescription(item.availabilityTier)}</span>
                    </span>
                  ),
                },
                {
                  label: 'Compound name',
                  renderer: item => item.compoundName || 'n/a',
                },
                {
                  label: 'CAS number',
                  renderer: item => item.casNumber || 'n/a',
                },
              ]}
              data={item}
            />
          </ContextualCard.Main>

          <ContextualCard.Floating className={cls.extendedInfoActions}>
            <Button minimal icon="cross" onClick={onClick} />
          </ContextualCard.Floating>
        </ContextualCard>
      ),
    };
  };
}

function buildAvailabilityTagProps(availabilityTier) {
  if (availabilityTier < 4) {
    return {
      intent: Intent.SUCCESS,
      icon: 'tick',
      text: 'In stock',
    };
  }

  if (availabilityTier === 4) {
    return {
      intent: Intent.WARNING,
      icon: 'lab-test',
      text: 'Synthesis',
    };
  }

  if (availabilityTier === 5) {
    return {
      icon: 'circle',
      text: 'Custom',
    };
  }

  return {
    text: 'Unknown',
  };
}

const schema = [
  {
    label: 'SM PRICE',
    renderer: item =>
      isNumber(item.price) ? (
        <>
          <span className={cls.dimmed}>$</span>
          {item.price}
        </>
      ) : (
        'n/a'
      ),
    headingCellProps: {
      className: cls.numericalCell,
    },
    cellProps: item => ({
      className: cx(cls.numericalCell, { [cls.mono]: isNumber(item.price) }),
    }),
  },
  {
    label: 'AMOUNT',
    renderer: item => {
      const [amount] = buildAmount(item.amount);
      return amount;
    },
    headingCellProps: {
      className: cx(cls.numericalCell, cls.rightPaddinglessCell),
    },
    cellProps: item => ({
      className: cx(cls.numericalCell, cls.rightPaddinglessCell, {
        [cls.monoCell]: isNumber(item.amount),
      }),
    }),
  },
  {
    renderer: item => {
      const [, unit] = buildAmount(item.amount);
      return unit;
    },
    headingCellProps: {
      className: cls.leftPaddinglessCell,
    },
    cellProps: {
      className: cx(cls.leftPaddinglessCell, cls.monoCell, cls.dimmed),
    },
  },
  {
    label: 'PRICE/GRAM',
    renderer: item => {
      if (isNumber(item.amount) && isNumber(item.price)) {
        return (
          <>
            <span className={cls.dimmed}>$</span>
            {(item.price / item.amount).toFixed(2)}
          </>
        );
      }
      return 'n/a';
    },
    headingCellProps: {
      className: cls.leftPaddinglessCell,
    },
  },
  {
    label: 'SUPPLIER',
    renderer: item => (
      <span className={cls.supplier}>
        <span>{item.supplierName ? truncate(item.supplierName, { length: 25 }) : 'n/a'}</span>
        {item.selectedSupplier && (
          <Tag round minimal intent={Intent.SUCCESS} icon="tick">
            Selected
          </Tag>
        )}
      </span>
    ),
  },
  {
    label: 'AVAILABILITY',
    renderer: item => {
      const { text, ...tagProps } = buildAvailabilityTagProps(item.availabilityTier);
      return (
        <Tag round minimal {...tagProps}>
          {text}
        </Tag>
      );
    },
  },
];

function PriceList({ data, selectedDatasets = [], className, loading }) {
  const items = React.useMemo(
    () => buildItems(data, selectedDatasets),
    [JSON.stringify(data), JSON.stringify(selectedDatasets)]
  );

  const [expansionMap, setExpansionMap] = React.useState({});
  const onRowClick = React.useCallback(
    (item, idx) =>
      setExpansionMap({
        ...expansionMap,
        [String(idx)]: !expansionMap[String(idx)],
      }),
    [JSON.stringify(expansionMap)]
  );
  const rowProps = React.useCallback(buildRowProps(expansionMap, onRowClick), [
    JSON.stringify(expansionMap),
  ]);

  return (
    <Table
      className={cx(cls.table, className)}
      schema={schema}
      items={items}
      interactive
      striped={false}
      loading={loading}
      rowProps={rowProps}
      tableProps={{
        striped: false,
      }}
      emptyStateProps={{
        title: 'No prices',
        description: 'There is no prices to show.',
      }}
      onRowClick={onRowClick}
    />
  );
}

PriceList.propTypes = {
  data: PropTypes.array,
  selectedDatasets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      selected_suppliers: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  className: PropTypes.string,
  loading: PropTypes.bool,
};

PriceList.defaultProps = {
  loading: false,
};

export default React.memo(PriceList);
